import { AsyncProps } from "react-select/async";
import { AsyncSelectInput } from "./AsyncSelectInput";
import { productsHttp } from "../../../module/products/service/products-http";
import { config } from "../../../config";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../module/auth/authSelectors";
import { ProductListItem } from "../../../module/products/models/product";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  classification: string;
  onChange?: (value: any) => void;
  label?: string;
  helperText?: string;
  filterProductsFunction?: (products: ProductListItem[]) => ProductListItem[];
} & Omit<AsyncProps<any, any, any>, "defaultOptions">;

export const SelectProductInput = ({
  name,
  classification,
  filterProductsFunction,
  label = "Product",
  helperText,
  ...rest
}: Props) => {
  const { watch } = useFormContext();
  const selectedValue = watch(name);
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  // With isMulti the form field holds an array of ids, otherwise a single id.
  const selectedProductIds: string[] = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue
    ? [selectedValue]
    : [];

  const retrieveAsyncOptions = async (inputValue: string) => {
    try {
      const products = await productsHttp.getProductList_({
        filters: {
          classification,
          ...(inputValue ? { name: inputValue } : {}),
        },
        url: `${config.apiEndpoint}/organizations/${organization}/shop-products`,
      });

      // GET products returns paginated result, so we need to check if the selected products are in the list
      // If one is not, we need to fetch that product from the API
      for (const selectedProductId of selectedProductIds) {
        const selectedProduct = products.data.find(
          (p) => p.id === selectedProductId
        );
        if (selectedProduct) {
          continue;
        }
        const product = await productsHttp.getProduct(
          organization,
          selectedProductId
        );
        if (product) {
          products.data.push({
            categoryName: product.categoryName,
            id: product.id,
            name: product.name,
            title: product.title,
            description: product.description,
            imageUrl: product?.imageUrl || "",
            price: {
              amount: product.priceAmount,
              currency: product.priceCurrency,
            },
          });
        }
      }

      const _products = filterProductsFunction
        ? filterProductsFunction(products.data)
        : products.data;

      const options = _products
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((product) => ({
          label: product.name,
          value: product.id,
        }));

      // A "None" entry only makes sense for a single select; clearing a multi
      // select is done by removing the chips.
      if (!rest.isMulti) {
        options.unshift({
          label: "None",
          value: "",
        });
      }

      return options;
    } catch (error) {
      return [];
    }
  };

  return (
    <AsyncSelectInput
      name={name}
      retrieveAsyncOptions={retrieveAsyncOptions}
      label={label}
      helperText={helperText}
      {...rest}
    />
  );
};
